import {
  Component,
  OnInit,
  OnChanges,
  Input,
  SimpleChanges,
} from '@angular/core';

import { TranslationsLibService } from '@nutricontrol/app360-shared';
import { MagnitudeValuePrecisionPipe } from '../../pipes/magnitude-value-precision.pipe';

@Component({
  selector: 'app-meteo-grid',
  templateUrl: './meteo-grid.component.html',
  styleUrls: ['./meteo-grid.component.scss'],
})
export class MeteoGridComponent implements OnInit, OnChanges {
  @Input() meteoRows: MeteoGridRows[] = [];
  @Input() terminalType:
    | 'climate'
    | 'irrigation'
    | 'configurable'
    | 'datalogger' = 'irrigation';
  meteoRowClass: string;
  cardClass: { [key: string]: boolean };
  iconsMap: { [key: string]: string } = {};

  constructor(
    private translationsLib: TranslationsLibService,
    private magnitudeValuePrecision: MagnitudeValuePrecisionPipe
  ) {}

  ngOnInit() {
    this.cardClass = this.getCardClass();
    this.meteoRowClass = this.getMeteoRowClass();
    this.initializeIconsMap();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.meteoRows) {
      this.meteoRowClass = this.getMeteoRowClass();
    }
  }

  public getFieldValue(field: MeteoGridRows): string {
    let value = '';

    if (this.terminalType === 'climate') {
      const valueMappings = {
        TRUE: this.translationsLib.get('climate_desktop_yes'),
        FALSE: this.translationsLib.get('climate_desktop_no'),
        TRAMO_0: this.translationsLib.get('climate_desktop_level0'),
        TRAMO_1: this.translationsLib.get('climate_desktop_level1'),
        TRAMO_2: this.translationsLib.get('climate_desktop_level2'),
        TRAMO_3: this.translationsLib.get('climate_desktop_level3'),
        TRAMO_4: this.translationsLib.get('climate_desktop_level4'),
        TRAMO_5: this.translationsLib.get('climate_desktop_level5'),
      };

      value = valueMappings.hasOwnProperty(field.value)
        ? valueMappings[field.value]
        : String(field.value);
    }

    value = this.magnitudeValuePrecision.transform(value, field.key, true);

    return value;
  }

  public getSensorIcon(key: string): string {
    return this.iconsMap[key] || '';
  }

  public isVirtualOrigin(field: MeteoGridRows): boolean {
    return field.origin === 'VIRTUAL';
  }

  public getFieldUnit(field: MeteoGridRows): string {
    if (
      field.key === 'SUNRISE' ||
      field.key === 'SUNSET' ||
      field.key === 'WIND_DIRECTION'
    ) {
      return '';
    }

    return field.unit || '';
  }

  public getDataHeader(field: MeteoGridRows): string {
    if (field.key.startsWith('Met_')) {
      return this.translationsLib.get('climate_' + field.key);
    }
    return this.translationsLib.get('climate_meteo_grid_' + field.key);
  }

  public getVirtualTooltip(): string {
    return this.translationsLib.get('irrigation_grid_virtual_title');
  }

  private getCardClass(): { [key: string]: boolean } {
    return {
      'irrigation-card-border': this.terminalType === 'irrigation',
      'climate-card-border': this.terminalType === 'climate',
      'configurable-card-border': this.terminalType === 'configurable',
      'datalogger-card-border': this.terminalType === 'datalogger',
    };
  }

  private getMeteoRowClass(): string {
    const length = this.meteoRows.length;
    if (length === 1) {
      return 'separator-custom col-12';
    } else if (length >= 2 && length <= 5) {
      return 'separator-custom col-6 col-sm-6';
    } else if (length === 6) {
      return 'separator-custom-case-6 col-6 col-sm-4';
    } else if (length === 7) {
      return 'separator-custom-case-7 col-6 col-sm-4';
    } else if (length === 8) {
      return 'separator-custom col-6 col-sm-6 col-xl-3';
    } else if (length === 9) {
      return 'separator-custom-case-9 col-6 col-sm-4';
    } else if (length >= 10 && length <= 11) {
      return 'separator-custom col-6 col-sm-6';
    } else {
      return 'separator-custom col-6 col-sm-4 col-xl-3';
    }
  }

  private initializeIconsMap(): void {
    this.iconsMap = {
      RAIN: 'RAIN',
      ET0: 'ET0',
      TEMPERATURE: 'TEMPERATURE',
      ACCUMULATED_RADIATION: 'ACCUMULATED_RADIATION',
      HUMIDITY: 'HUMIDITY',
      PLUVIOMETER: 'PLUVIOMETER',
      RADIATION: 'RADIATION',
      WIND_DIRECTION: 'WIND_DIRECTION',
      WIND_VELOCITY: 'WIND_VELOCITY',
      WIND_LEVEL_0: 'WIND_LEVEL_0',
      WIND_LEVEL_1: 'WIND_LEVEL_1',
      SUNRISE: 'SUNRISE',
      SUNSET: 'SUNSET',
      Met_time: 'CLOCK',
      Met_temp: 'TEMPERATURE',
      Met_rain: 'DRIZZLE',
      Met_windDir: 'SIGNPOST',
      Met_instRad: 'ACCUMULATED_RADIATION',
      Met_freezeAl: 'CHILL_HOURS',
      Met_pluv: 'PLUVIOMETER',
      Met_day: 'BRIGHTNESS_ALT',
      Met_stormAl1: 'WIND_VELOCITY',
      Met_stormAl2: 'WIND_VELOCITY',
      Met_hum: 'HD',
      Met_windSpeed: 'WIND_VELOCITY',
      Met_cummRad: 'ACCUMULATED_RADIATION',
    };
  }
}

export interface MeteoGridRows {
  value: string;
  key: string;
  unit: string;
  origin: string;
}
